/**
 * Config registry of the `extras2` admin-module group (marketing campaigns / lists / segments / SMS / templates /
 * surveys, Royal Club editors, education courses). It extends the marketing resource registry
 * (`../marketing/marketingResources`) in three ways:
 *   - `LIST_RESOURCES`: new list-capable resources that are injected into the marketing `RESOURCES` map so the shared
 *     `ResourceListBody` can render them;
 *   - `DETAILS`: per-resource detail configuration (fields come from the record, plus stats, related lists, actions with
 *     typed inputs and an edit entry point);
 *   - `FORMS`: config-driven create / edit forms (flat, dotted and localized fields, remote pickers).
 * Existing marketing resources only get their row `open` target redirected to the richer `Extras2DetailScreen`.
 * Pure data + small helpers (no React). Importing this module applies the injection once.
 *
 * Detail config:
 *   path(id, item)   GET path (null -> the record passed in route params is shown)   keys  wrapper keys of the record
 *   statsPath(id)    optional extra GET shown as a statistics card                     sections  render nested objects / arrays as cards
 *   related          [{resource, icon, parentId?: true, queryKey?: string}] list buttons
 *   buttons          [{id, icon, screen, params(record, id)}] navigation buttons
 *   actions          [{id, icon, tone, method, path(record, id, ctx), body(record, values), when(record), inputs[], closes}]
 *   form             key of FORMS (native edit)  editScreen {screen, params(record, id)} (custom editor instead)
 *
 * Form config:
 *   title key, load {path(id), keys}, create {path(ctx)}, update {path(id, ctx), method}, fields[],
 *   transform(body, values, record), afterSave(api, saved, values, ctx), detailResource (open detail after create)
 *   field: {key (dotted ok), type: text|multiline|number|switch|chips|multi|remote|localized|datetime|tags,
 *           required, options, createOnly, editOnly, nullable, keyboard, initial, virtual, remote:{path, keys, labelKeys, multi}}
 */
import {RESOURCES, idOf} from '../marketing/marketingResources';

const enc = (value) => encodeURIComponent(String(value));

/** Value of a dotted path. */
export const getIn = (obj, path) => String(path).split('.').reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), obj);

/**
 * Writes a dotted path, creating the intermediate objects.
 * @param {object} target Object to write.
 * @param {string} path Dotted path.
 * @param {*} value Value.
 * @returns {object} The target.
 */
export const setIn = (target, path, value) => {
    const parts = String(path).split('.');
    let node = target;
    for (let i = 0; i < parts.length - 1; i += 1) {
        if (!node[parts[i]] || typeof node[parts[i]] !== 'object' || Array.isArray(node[parts[i]])) {
            node[parts[i]] = {};
        }
        node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
    return target;
};

const STATUS_FILTER = (values) => [{param: 'status', labelKey: 'adminmod_marketing_f_status', values}];

/** New list-capable resources (marketing resource format) injected into the marketing `RESOURCES`. */
export const LIST_RESOURCES = {
    emailCampaigns: {
        titleKey: 'adminmod_extras2_r_emailCampaigns',
        icon: 'rocket-launch-outline',
        tone: 'violet',
        list: {
            path: () => '/email-campaigns',
            keys: ['campaigns', 'items'],
            paged: true,
            pageSize: 20,
            filters: STATUS_FILTER(['draft', 'scheduled', 'sending', 'sent', 'paused', 'canceled', 'failed']),
        },
        row: {title: ['name', 'content.subject'], subtitle: ['content.subject', 'subject'], meta: ['scheduledAt', 'sentAt', 'updatedAt'], badge: 'status', icon: 'email-fast-outline'},
    },
    segments: {
        titleKey: 'adminmod_extras2_r_segments',
        icon: 'account-filter-outline',
        tone: 'info',
        list: {path: () => '/marketing/segments', keys: ['segments', 'items'], search: true, paged: true, pageSize: 25},
        row: {title: ['name'], subtitle: ['description'], meta: ['lastCount', 'kind'], badge: 'origin', icon: 'account-filter-outline'},
    },
    segmentMembers: {
        titleKey: 'adminmod_extras2_r_segmentMembers',
        icon: 'account-multiple-outline',
        tone: 'info',
        idKeys: ['contactId', 'email', 'phone'],
        list: {path: (p) => `/marketing/segments/${enc(p.parentId)}/members`, keys: ['members', 'items'], paged: true, pageSize: 25, filters: [{param: 'channel', labelKey: 'adminmod_extras2_f_channel', values: ['email', 'sms', 'whatsapp']}]},
        row: {title: ['name', 'email', 'phone'], subtitle: ['email', 'phone'], meta: ['contactType'], icon: 'account-outline'},
    },
    smsCampaigns: {
        titleKey: 'adminmod_extras2_r_smsCampaigns',
        icon: 'message-processing-outline',
        tone: 'success',
        list: {
            path: () => '/sms/campaigns',
            keys: ['items', 'campaigns'],
            search: true,
            paged: true,
            pageSize: 20,
            filters: STATUS_FILTER(['draft', 'scheduled', 'sending', 'sent', 'paused', 'canceled', 'failed']),
        },
        row: {title: ['name'], subtitle: ['message.body'], meta: ['schedule.sendAt', 'updatedAt'], badge: 'status', icon: 'message-processing-outline'},
    },
    smsRecipients: {
        titleKey: 'adminmod_extras2_r_smsRecipients',
        icon: 'cellphone-message',
        tone: 'success',
        list: {path: (p) => `/sms/campaigns/${enc(p.parentId)}/recipients`, keys: ['items', 'recipients'], paged: true, pageSize: 25, filters: STATUS_FILTER(['queued', 'sent', 'delivered', 'failed'])},
        row: {title: ['name', 'phone'], subtitle: ['phone'], meta: ['sentAt'], badge: 'status', icon: 'cellphone'},
    },
    loyaltyMembers: {
        titleKey: 'adminmod_marketing_page_loyalty-members',
        icon: 'account-group-outline',
        tone: 'violet',
        list: {
            path: () => '/loyalty/members',
            keys: ['members', 'items', 'memberships'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: STATUS_FILTER(['active', 'paused', 'blocked', 'closed']),
        },
        row: {title: ['guest.name', 'guest.email', 'guest.phone', 'memberCode'], subtitle: ['guest.email', 'guest.phone'], meta: ['memberCode', 'tier', 'counters.points'], badge: 'status', icon: 'account-heart-outline'},
    },
    loyaltyLedger: {
        titleKey: 'adminmod_extras2_r_loyaltyLedger',
        icon: 'book-open-outline',
        tone: 'violet',
        list: {path: () => '/loyalty/ledger', keys: ['entries', 'items'], paged: true, pageSize: 25},
        row: {title: ['type'], subtitle: ['reason', 'orderId'], meta: ['points', 'stamps', 'cashback', 'createdAt'], icon: 'swap-vertical'},
    },
    consents: {
        titleKey: 'adminmod_extras2_r_consents',
        icon: 'shield-check-outline',
        tone: 'danger',
        idKeys: ['_id', 'id', 'address'],
        list: {
            path: () => '/marketing/consents',
            keys: ['items', 'consents'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: [{param: 'channel', labelKey: 'adminmod_extras2_f_channel', values: ['sms', 'whatsapp', 'push', 'voice']}],
        },
        row: {title: ['address'], subtitle: ['channel', 'reason'], meta: ['updatedAt'], badge: 'status', icon: 'shield-account-outline'},
    },
    insightContacts: {
        titleKey: 'adminmod_extras2_r_insightContacts',
        icon: 'account-search-outline',
        tone: 'info',
        list: {
            path: () => '/marketing/insights',
            keys: ['items'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: [
                {param: 'band', labelKey: 'adminmod_extras2_f_band', values: ['champion', 'loyal', 'big_spender', 'promising', 'at_risk', 'lapsed', 'new', 'prospect']},
                {param: 'channel', labelKey: 'adminmod_extras2_f_channel', values: ['email', 'sms', 'whatsapp']},
            ],
        },
        row: {title: ['name', 'email', 'phone'], subtitle: ['email', 'phone'], meta: ['totalSpent', 'lastOrderAt'], badge: 'rfm.band', icon: 'account-outline'},
    },
    insightSummary: {
        titleKey: 'adminmod_extras2_r_insightSummary',
        icon: 'chart-donut',
        tone: 'info',
        singleton: {path: '/marketing/insights/summary', keys: ['summary']},
    },
    aiRecommendations: {
        titleKey: 'adminmod_extras2_r_aiRecommendations',
        icon: 'lightbulb-on-outline',
        tone: 'warning',
        idKeys: ['cardId', 'id'],
        list: {path: () => '/marketing/ai/recommendations', keys: ['cards'], paged: false},
        row: {title: ['title'], subtitle: ['rationale'], meta: ['suggestedChannel', 'estimatedReach'], icon: 'lightbulb-on-outline'},
    },
};

/** Detail configuration per resource key. */
export const DETAILS = {
    lists: {
        path: (id) => `/marketing/lists/${enc(id)}`,
        keys: ['list'],
        form: 'lists',
        buttons: [{id: 'subscribers', icon: 'account-multiple-plus-outline', screen: 'Extras2ResourceScreen', params: (_r, id) => ({resource: 'subscribers', query: {listId: id}, titleKey: 'adminmod_marketing_page_feliratkozok'})}],
        actions: [
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_r, id) => `/marketing/lists/${enc(id)}/duplicate`, closes: true},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_r, id) => `/marketing/lists/${enc(id)}`, closes: true},
        ],
    },
    emailCampaigns: {
        path: (id) => `/email-campaigns/${enc(id)}`,
        keys: ['campaign'],
        statsPath: (id) => `/email-campaigns/${enc(id)}/stats`,
        form: 'emailCampaigns',
        canEdit: (rec) => ['draft', 'scheduled', 'paused'].includes(String(rec?.status || 'draft')),
        buttons: [{id: 'statistics', icon: 'chart-box-outline', screen: 'MarketingCampaignDetailScreen', params: (_r, id) => ({id})}],
        actions: [
            {id: 'test', icon: 'email-fast-outline', method: 'POST', path: (_r, id) => `/email-campaigns/${enc(id)}/test`, body: (_r, v) => ({to: v.to}), inputs: [{key: 'to', required: true, keyboard: 'email-address'}], when: (r) => !['sending', 'sent'].includes(r.status)},
            {id: 'schedule', icon: 'send-outline', method: 'POST', path: (_r, id) => `/email-campaigns/${enc(id)}/schedule`, body: () => ({}), when: (r) => ['draft', 'paused', undefined, null, ''].includes(r.status)},
            {id: 'pause', icon: 'pause', method: 'POST', path: (_r, id) => `/email-campaigns/${enc(id)}/pause`, when: (r) => ['sending', 'scheduled'].includes(r.status)},
            {id: 'resume', icon: 'play', method: 'POST', path: (_r, id) => `/email-campaigns/${enc(id)}/resume`, when: (r) => r.status === 'paused'},
            {id: 'cancel', icon: 'close-circle-outline', tone: 'danger', method: 'POST', path: (_r, id) => `/email-campaigns/${enc(id)}/cancel`, when: (r) => ['sending', 'scheduled', 'paused'].includes(r.status)},
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_r, id) => `/email-campaigns/${enc(id)}/duplicate`, body: () => ({}), closes: true},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_r, id) => `/email-campaigns/${enc(id)}`, when: (r) => !['sending', 'scheduled'].includes(r.status), closes: true},
        ],
    },
    segments: {
        path: (id) => `/marketing/segments/${enc(id)}`,
        keys: ['segment'],
        editScreen: {screen: 'Extras2SegmentBuilderScreen', params: (_r, id) => ({id})},
        canEdit: (rec) => rec?.kind !== 'system',
        related: [{resource: 'segmentMembers', icon: 'account-multiple-outline', parentId: true}],
        actions: [
            {id: 'refreshCount', icon: 'refresh', method: 'POST', path: (_r, id) => `/marketing/segments/${enc(id)}/refresh-count`, body: () => ({})},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_r, id) => `/marketing/segments/${enc(id)}`, when: (r) => r.kind !== 'system', closes: true},
        ],
    },
    segmentMembers: {path: null, keys: []},
    smsCampaigns: {
        path: (id) => `/sms/campaigns/${enc(id)}`,
        keys: ['campaign'],
        statsPath: (id) => `/sms/campaigns/${enc(id)}/stats`,
        form: 'smsCampaigns',
        canEdit: (rec) => ['draft', 'scheduled', 'paused', undefined].includes(rec?.status),
        related: [{resource: 'smsRecipients', icon: 'cellphone-message', parentId: true}],
        actions: [
            {id: 'testSms', icon: 'message-processing-outline', method: 'POST', path: (_r, id) => `/sms/campaigns/${enc(id)}/test-send`, body: (_r, v) => ({phone: v.phone}), inputs: [{key: 'phone', required: true, keyboard: 'phone-pad'}], when: (r) => !['sending', 'sent'].includes(r.status)},
            {id: 'schedule', icon: 'send-outline', method: 'POST', path: (_r, id) => `/sms/campaigns/${enc(id)}/schedule`, body: () => ({}), when: (r) => ['draft', undefined, null, ''].includes(r.status)},
            {id: 'pause', icon: 'pause', method: 'POST', path: (_r, id) => `/sms/campaigns/${enc(id)}/pause`, body: () => ({}), when: (r) => ['sending', 'scheduled'].includes(r.status)},
            {id: 'resume', icon: 'play', method: 'POST', path: (_r, id) => `/sms/campaigns/${enc(id)}/resume`, body: () => ({}), when: (r) => r.status === 'paused'},
            {id: 'cancel', icon: 'close-circle-outline', tone: 'danger', method: 'POST', path: (_r, id) => `/sms/campaigns/${enc(id)}/cancel`, body: () => ({}), when: (r) => ['sending', 'scheduled', 'paused'].includes(r.status)},
        ],
    },
    smsRecipients: {path: null, keys: []},
    emailTemplates: {
        path: (id) => `/campaign-templates/${enc(id)}`,
        keys: ['template'],
        editScreen: {screen: 'Extras2TemplateEditorScreen', params: (_r, id) => ({id})},
        canEdit: (rec) => rec?.scope !== 'system' && !rec?.isSystem,
        actions: [
            {id: 'useTemplate', icon: 'rocket-launch-outline', method: 'POST', path: (_r, id) => `/campaign-templates/${enc(id)}/apply`, body: () => ({}), opens: 'emailCampaigns'},
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_r, id) => `/campaign-templates/${enc(id)}/duplicate`, body: () => ({}), closes: true},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_r, id) => `/campaign-templates/${enc(id)}`, when: (r) => r.scope !== 'system' && !r.isSystem, closes: true},
        ],
    },
    surveys: {
        path: (id) => `/marketing/surveys/${enc(id)}`,
        keys: ['survey'],
        statsPath: (id) => `/marketing/surveys/${enc(id)}/stats`,
        editScreen: {screen: 'Extras2SurveyEditorScreen', params: (_r, id) => ({id})},
        canEdit: (rec) => rec?.status !== 'archived',
        buttons: [{id: 'results', icon: 'poll', screen: 'Extras2SurveyResultsScreen', params: (_r, id) => ({id})}],
        actions: [],
    },
    loyaltyPrograms: {
        path: (id) => `/loyalty/programs/${enc(id)}`,
        keys: ['program'],
        form: 'loyaltyPrograms',
        actions: [],
    },
    rewards: {
        path: (id) => `/loyalty/rewards/${enc(id)}`,
        keys: ['reward'],
        form: 'rewards',
        actions: [],
    },
    events: {
        path: null,
        keys: [],
        form: 'events',
        canEdit: (rec) => !['cancelled', 'canceled', 'completed'].includes(String(rec?.status)),
        actions: [],
    },
    offers: {path: null, keys: [], form: 'offers', actions: []},
    membershipPlans: {path: null, keys: [], form: 'membershipPlans', actions: []},
    loyaltyMembers: {
        path: (id) => `/loyalty/members/${enc(id)}`,
        keys: [],
        sections: true,
        actions: [
            {
                id: 'adjust',
                icon: 'plus-minus-variant',
                method: 'POST',
                path: (_r, id) => `/loyalty/members/${enc(id)}/adjust`,
                body: (_r, v) => {
                    const body = {reason: v.reason};
                    ['points', 'stamps', 'cashback'].forEach((key) => {
                        if (v[key] !== undefined && String(v[key]).trim() !== '' && Number.isFinite(Number(v[key])) && Number(v[key]) !== 0) {
                            body[key] = Number(v[key]);
                        }
                    });
                    return body;
                },
                inputs: [
                    {key: 'points', type: 'number', signed: true},
                    {key: 'stamps', type: 'number', signed: true},
                    {key: 'cashback', type: 'number', signed: true},
                    {key: 'reason', type: 'multiline', required: true},
                ],
            },
            {id: 'recompute', icon: 'calculator-variant-outline', method: 'POST', path: (_r, id) => `/loyalty/members/${enc(id)}/recompute`, body: () => ({})},
        ],
        buttons: [{id: 'ledger', icon: 'book-open-outline', screen: 'Extras2ResourceScreen', params: (_r, id) => ({resource: 'loyaltyLedger', query: {membershipId: id}})}],
    },
    loyaltyLedger: {path: null, keys: []},
    consents: {
        path: null,
        keys: [],
        actions: [
            {id: 'optIn', icon: 'check-circle-outline', method: 'POST', path: () => '/marketing/consents', body: (r) => ({channel: r.channel, address: r.address, action: 'opt_in'}), when: (r) => r.status === 'opted_out', closes: true},
            {id: 'optOut', icon: 'close-circle-outline', tone: 'danger', method: 'POST', path: () => '/marketing/consents', body: (r) => ({channel: r.channel, address: r.address, action: 'opt_out', reason: 'manual'}), when: (r) => r.status === 'opted_in', closes: true},
        ],
    },
    insightContacts: {path: null, keys: []},
    aiRecommendations: {
        path: null,
        keys: [],
        actions: [
            {id: 'applyCard', icon: 'rocket-launch-outline', method: 'POST', path: (r) => `/marketing/ai/recommendations/${enc(r.cardId)}/apply`, body: () => ({}), closes: true},
            {id: 'dismissCard', icon: 'close-circle-outline', tone: 'danger', method: 'POST', path: (r) => `/marketing/ai/recommendations/${enc(r.cardId)}/dismiss`, body: () => ({}), closes: true},
        ],
    },
    courses: {
        path: (id) => `/education/courses/${enc(id)}`,
        keys: ['course'],
        statsPath: (id) => `/education/courses/${enc(id)}/stats`,
        form: 'courses',
        related: [
            {resource: 'courseSessions', icon: 'calendar-clock-outline', parentId: true},
            {resource: 'courseRegistrations', icon: 'account-multiple-outline', parentId: true},
        ],
        actions: [],
    },
    courseSessions: {
        path: null,
        keys: [],
        form: 'courseSessions',
        actions: [
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (r, id, ctx) => `/education/courses/${enc(ctx.parentId)}/sessions/${enc(id)}`, closes: true},
        ],
    },
};

const localizedField = (key) => ({key, type: 'localized'});

/** Create / edit forms. */
export const FORMS = {
    lists: {
        titleKey: 'adminmod_marketing_page_listak',
        load: {path: (id) => `/marketing/lists/${enc(id)}`, keys: ['list']},
        create: {path: () => '/marketing/lists'},
        update: {path: (id) => `/marketing/lists/${enc(id)}`, method: 'PUT'},
        fields: [{key: 'name', required: true}, {key: 'description', type: 'multiline'}],
    },
    emailCampaigns: {
        titleKey: 'adminmod_extras2_r_emailCampaigns',
        load: {path: (id) => `/email-campaigns/${enc(id)}`, keys: ['campaign']},
        create: {path: () => '/email-campaigns'},
        update: {path: (id) => `/email-campaigns/${enc(id)}`, method: 'PATCH'},
        detailResource: 'emailCampaigns',
        fields: [
            {key: 'name', required: true},
            {key: 'content.subject', required: true},
            {key: 'content.preheader'},
            {key: '_templateId', type: 'remote', virtual: true, labelKey: 'adminmod_extras2_f_templateId', remote: {path: '/campaign-templates', keys: ['restaurant', 'system', 'templates'], labelKeys: ['name', 'title'], flatten: true}},
            {key: 'sender.fromName'},
            {key: 'sender.fromEmail', keyboard: 'email-address'},
            {key: 'sender.replyTo', keyboard: 'email-address'},
            {key: 'audience.includeLists', type: 'remote', remote: {path: '/marketing/lists?limit=100', keys: ['lists', 'items'], labelKeys: ['name'], multi: true}, labelKey: 'adminmod_extras2_f_lists'},
            {key: 'audience.segmentId', type: 'remote', nullable: true, remote: {path: '/marketing/segments?limit=100', keys: ['segments', 'items'], labelKeys: ['name']}, labelKey: 'adminmod_extras2_f_segment'},
            {key: 'audience.contactTypes', type: 'multi', options: ['User', 'Lead', 'Guest', 'Customer', 'Partner', 'ServiceModuleCustomer', 'MarketingSubscriber']},
            {key: 'audience.includeTags', type: 'tags'},
            {key: 'schedule.sendAt', type: 'datetime', nullable: true},
            {key: 'compliance.postalFooter', type: 'multiline'},
        ],
        transform: (body, values, record, ctx) => {
            if (!ctx.isEdit) {
                body.type = 'regular';
                body.content = {source: 'raw', ...(body.content || {})};
            } else if (record?.content) {
                body.content = {...record.content, ...(body.content || {})};
            }
            return body;
        },
        afterSave: async (api, saved, values, ctx) => {
            const campaignId = ctx.savedId;
            if (values._templateId && campaignId) {
                await api(`/campaign-templates/${enc(values._templateId)}/apply`, {method: 'POST', body: {campaignId}});
                const fresh = await api(`/email-campaigns/${enc(campaignId)}`, {method: 'GET'});
                const content = (fresh && (fresh.campaign || fresh.data?.campaign || fresh)) || {};
                const nextContent = {...(content.content || {})};
                if (String(values['content.subject'] || '').trim()) {
                    nextContent.subject = String(values['content.subject']).trim();
                }
                if (String(values['content.preheader'] || '').trim()) {
                    nextContent.preheader = String(values['content.preheader']).trim();
                }
                await api(`/email-campaigns/${enc(campaignId)}`, {method: 'PATCH', body: {content: nextContent}});
            }
        },
    },
    smsCampaigns: {
        titleKey: 'adminmod_extras2_r_smsCampaigns',
        load: {path: (id) => `/sms/campaigns/${enc(id)}`, keys: ['campaign']},
        create: {path: () => '/sms/campaigns'},
        update: {path: (id) => `/sms/campaigns/${enc(id)}`, method: 'PUT'},
        detailResource: 'smsCampaigns',
        fields: [
            {key: 'name', required: true},
            {key: 'message.body', type: 'multiline', required: true},
            {key: 'audience.segmentId', type: 'remote', nullable: true, remote: {path: '/marketing/segments?limit=100', keys: ['segments', 'items'], labelKeys: ['name']}, labelKey: 'adminmod_extras2_f_segment'},
            {key: 'audience.contactTypes', type: 'multi', options: ['Guest', 'Customer', 'Lead', 'ServiceModuleCustomer']},
            {key: 'schedule.sendAt', type: 'datetime', nullable: true},
            {key: 'compliance.appendOptOutHint', type: 'switch', initial: true},
        ],
    },
    loyaltyPrograms: {
        titleKey: 'adminmod_marketing_page_loyalty-programs',
        load: {path: (id) => `/loyalty/programs/${enc(id)}`, keys: ['program']},
        create: {path: () => '/loyalty/programs'},
        update: {path: (id) => `/loyalty/programs/${enc(id)}`, method: 'PUT'},
        fields: [
            {key: 'name', required: true},
            {key: 'type', type: 'chips', options: ['points', 'stamps', 'tiers', 'challenges', 'cashback', 'subscription', 'referral', 'experience', 'hybrid'], initial: 'points', createOnly: false},
            {key: 'autoEnroll', type: 'switch'},
            {key: 'consentPolicyVersion'},
            {key: 'eligibility.orderTypes', type: 'multi', options: ['online', 'dineIn', 'takeAway']},
            {key: 'eligibility.minNet', type: 'number'},
            {key: 'budget.monthlyCap', type: 'number'},
            {key: 'budget.dailyCap', type: 'number'},
            {key: 'budget.perGuestMonthlyCap', type: 'number'},
            {key: 'budget.marginFloorPct', type: 'number'},
        ],
    },
    rewards: {
        titleKey: 'adminmod_marketing_page_loyalty-rewards',
        load: {path: (id) => `/loyalty/rewards/${enc(id)}`, keys: ['reward']},
        create: {path: () => '/loyalty/rewards'},
        update: {path: (id) => `/loyalty/rewards/${enc(id)}`, method: 'PUT'},
        fields: [
            {...localizedField('name'), required: true},
            localizedField('description'),
            {key: 'type', type: 'chips', options: ['free_item', 'discount_amount', 'discount_pct', 'experience', 'badge'], initial: 'free_item', required: true},
            {key: 'value', type: 'number'},
            {key: 'minSpend', type: 'number'},
            {key: 'costHuf', type: 'number'},
            {key: 'inventory.total', type: 'number'},
            {key: 'validityDays', type: 'number'},
            {key: 'perGuestLimit', type: 'number'},
            {key: 'channels', type: 'multi', options: ['dineIn', 'takeAway', 'online']},
            {key: 'imageUrl', keyboard: 'url'},
            {key: 'active', type: 'switch', initial: true},
        ],
    },
    events: {
        titleKey: 'adminmod_marketing_page_loyalty-events',
        create: {path: () => '/loyalty/events'},
        update: {path: (id) => `/loyalty/events/${enc(id)}`, method: 'PUT'},
        fields: [
            {key: 'title', required: true},
            {key: 'description', type: 'multiline', nullable: true},
            {key: 'startsAt', type: 'datetime', required: true},
            {key: 'endsAt', type: 'datetime', nullable: true},
            {key: 'venue', nullable: true},
            {key: 'capacity', type: 'number', nullable: true},
            {key: 'priceLabel', nullable: true},
            {key: 'imageUrl', keyboard: 'url', nullable: true},
            {key: 'minTier', nullable: true},
            {key: 'status', type: 'chips', options: ['draft', 'published'], initial: 'draft'},
        ],
    },
    offers: {
        titleKey: 'adminmod_marketing_page_loyalty-offers',
        create: {path: () => '/loyalty/offers'},
        update: {path: (id) => `/loyalty/offers/${enc(id)}`, method: 'PUT'},
        fields: [
            {key: 'placement', type: 'chips', options: ['venue', 'campaign', 'home_invite', 'next_best', 'geofence'], initial: 'venue', required: true, createOnly: true},
            {key: 'slug', createOnly: true},
            {key: 'title', required: true},
            {key: 'body', type: 'multiline', nullable: true},
            {key: 'ctaLabel', nullable: true},
            {key: 'imageUrl', keyboard: 'url', nullable: true},
            {key: 'priority', type: 'number'},
            {key: 'validFrom', type: 'datetime', nullable: true},
            {key: 'validTo', type: 'datetime', nullable: true},
            {key: 'status', type: 'chips', options: ['draft', 'published', 'archived'], initial: 'draft'},
        ],
    },
    membershipPlans: {
        titleKey: 'adminmod_extras2_r_membershipPlans',
        create: {path: () => '/loyalty/membership/plans'},
        update: {path: (id) => `/loyalty/membership/plans/${enc(id)}`, method: 'PUT'},
        fields: [
            {key: 'name', required: true},
            {key: 'description', type: 'multiline', nullable: true},
            {key: 'price.amount', type: 'number', required: true},
            {key: 'price.currency', initial: 'HUF'},
            {key: 'period.unit', type: 'chips', options: ['month', 'year', 'day'], initial: 'month'},
            {key: 'period.count', type: 'number', initial: '1'},
            {key: 'sortOrder', type: 'number'},
            {key: 'status', type: 'chips', options: ['draft', 'active'], initial: 'draft'},
        ],
    },
    courses: {
        titleKey: 'adminmod_marketing_page_oktatas-tanfolyamok',
        load: {path: (id) => `/education/courses/${enc(id)}`, keys: ['course']},
        create: {path: () => '/education/courses'},
        update: {path: (id) => `/education/courses/${enc(id)}`, method: 'PATCH'},
        fields: [
            {key: 'name', required: true},
            {key: 'code'},
            {key: 'description', type: 'multiline'},
            {key: 'type', type: 'chips', options: ['online', 'onsite', 'hybrid'], initial: 'onsite'},
            {key: 'startDate', type: 'datetime', required: true, dateOnly: true},
            {key: 'endDate', type: 'datetime', required: true, dateOnly: true},
            {key: 'registrationDeadline', type: 'datetime', dateOnly: true},
            {key: 'minParticipants', type: 'number'},
            {key: 'maxParticipants', type: 'number'},
            {key: 'location.name'},
            {key: 'location.city'},
            {key: 'meetingLink', keyboard: 'url'},
            {key: 'isPaid', type: 'switch'},
            {key: 'price', type: 'number'},
            {key: 'currency', initial: 'HUF'},
            {key: 'notes', type: 'multiline'},
        ],
    },
    courseSessions: {
        titleKey: 'adminmod_extras2_r_courseSessions',
        needsParent: true,
        create: {path: (ctx) => `/education/courses/${enc(ctx.parentId)}/sessions`},
        update: {path: (id, ctx) => `/education/courses/${enc(ctx.parentId)}/sessions/${enc(id)}`, method: 'PATCH'},
        fields: [
            {key: 'date', type: 'datetime', required: true, dateOnly: true},
            {key: 'startTime'},
            {key: 'endTime'},
            {key: 'topic'},
            {key: 'isOnline', type: 'switch'},
            {key: 'meetingLink', keyboard: 'url'},
            {key: 'notes', type: 'multiline'},
        ],
    },
};

let listContext = {};

/**
 * Remembers the parent id of the list being shown, so row-open targets of related lists can pass it on.
 * @param {object} context `{parentId}`.
 * @returns {void}
 */
export const setListContext = (context) => {
    listContext = context || {};
};

const openDetail = (resource) => ({
    screen: 'Extras2DetailScreen',
    params: (item, id) => ({resource, id, item, ...(listContext.parentId ? {parentId: listContext.parentId} : {})}),
});

/** Resources whose existing marketing registry entry gets its row-open target redirected. */
const REDIRECT_OPEN = ['lists', 'emailTemplates', 'surveys', 'loyaltyPrograms', 'rewards', 'events', 'offers', 'membershipPlans', 'courses', 'courseSessions'];

/**
 * Converts marketing-registry actions (single `input`, `body(item, input)`) to the extras2 action shape
 * (`inputs[]`, `body(record, values)`).
 * @param {object[]} list Marketing actions.
 * @returns {object[]} Adapted actions.
 */
const adaptActions = (list) => (list || []).map((action) => {
    const inputKey = action.input ? action.input.key : '';
    return {
        ...action,
        ...(action.input ? {inputs: [action.input]} : {}),
        ...(action.body ? {body: (record, values) => action.body(record, inputKey ? values[inputKey] : '')} : {}),
    };
});

let injected = false;

/** Injects the new list resources into the marketing registry and redirects the row-open target (idempotent). */
export const injectExtras2Resources = () => {
    if (injected) {
        return;
    }
    injected = true;
    Object.keys(LIST_RESOURCES).forEach((key) => {
        const cfg = LIST_RESOURCES[key];
        if (!cfg || RESOURCES[key]) {
            return;
        }
        RESOURCES[key] = {...cfg, open: cfg.open || openDetail(key)};
    });
    REDIRECT_OPEN.forEach((key) => {
        if (RESOURCES[key] && DETAILS[key]) {
            RESOURCES[key].open = openDetail(key);
            if (Array.isArray(DETAILS[key].actions) && DETAILS[key].actions.length === 0 && Array.isArray(RESOURCES[key].actions)) {
                DETAILS[key].actions = adaptActions(RESOURCES[key].actions);
            }
        }
    });
};

injectExtras2Resources();

/**
 * Record id for a detail / form route.
 * @param {string} resource Resource key.
 * @param {object} item Record.
 * @returns {string} Id.
 */
export const recordId = (resource, item) => idOf(item, RESOURCES[resource]?.idKeys);
